import { BetrokkenTrein, usePatchBusEvacuatie, usePostBusEvacuatie } from "@/entities/betrokken-treinen";
import { StationsAutocomplete } from "@/features/infra";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormErrorMessage,
    FormFieldInput,
    SelectFormField,
    TextAreaFormField,
} from "@/shared/components";
import { BusEvacuatieAanvraagDto, BusEvacuatieWijzigingDto } from "@/shared/types";
import { requiredNumberString } from "@/shared/utils/zod-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import z from "zod";

const vervoerderOptions = [
    { value: "Transvision", label: "Transvision" },
    { value: "DeWit", label: "De Wit" },
];

const busEvacuatieNewEditSchema = z.object({
    vervoerder: z.string().min(1, { message: "Vul een vervoerder in" }),
    aantalBussen: requiredNumberString("Vul minimaal 1 bus in"),
    opstelLocatie: z.string().nonempty({ message: "Vul een opstellocatie in" }),
    naarStation: z.string().nonempty({ message: "Vul het afzetstation in" }),
});

type BusEvacuatieNewEditData = z.infer<typeof busEvacuatieNewEditSchema>;

type BusEvacuatieNewEditDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dossierId: string;
    trein: BetrokkenTrein;
    defaultValues?: Partial<BusEvacuatieNewEditData>;
};

export function BusEvacuatieNewEditDialog({ dossierId, trein, defaultValues, open, onOpenChange }: BusEvacuatieNewEditDialogProps) {
    const isEdit = !!trein.busEvacuatie;
    const postBusEvacuatie = usePostBusEvacuatie();
    const patchBusEvacuatie = usePatchBusEvacuatie();

    const form = useForm<BusEvacuatieNewEditData>({
        resolver: zodResolver(busEvacuatieNewEditSchema),
        defaultValues: {
            vervoerder: defaultValues?.vervoerder ?? "",
            aantalBussen: defaultValues?.aantalBussen ?? String(trein.aantalReizigers ? Math.ceil(trein.aantalReizigers / 50) : 1),
            opstelLocatie: defaultValues?.opstelLocatie ?? "",
            naarStation: defaultValues?.naarStation ?? "",
        },
    });

    // Destructure formState here to ensure updates, see https://github.com/react-hook-form/react-hook-form/issues/1146
    const {
        formState: { dirtyFields, errors },
    } = form;

    function handleOpenChange(isOpen: boolean) {
        if (!isOpen) {
            form.reset();
        }
        onOpenChange(isOpen);
    }

    function onSubmit(data: BusEvacuatieNewEditData) {
        const mutateOptions = {
            onSuccess: () => handleOpenChange(false),
        };

        if (trein.busEvacuatie) {
            const dirtyKeys = Object.keys(dirtyFields) as Array<keyof Omit<BusEvacuatieNewEditData, "vervoerder">>;

            if (dirtyKeys.length === 0) {
                // no need to perform the mutation, because there are no changes
                handleOpenChange(false);
                return;
            }

            if (Number(data.aantalBussen) < trein.busEvacuatie.etas.length) {
                form.setError("root", { message: "Aantal bussen mag niet lager zijn dan het aantal geleverde bussen" });
                return;
            }

            const patchPayload = dirtyKeys.reduce((acc: BusEvacuatieWijzigingDto, key) => {
                if (key === "aantalBussen") {
                    acc["aantalBussen"] = Number(data[key]);
                } else {
                    acc[key] = data[key];
                }
                return acc;
            }, {});

            patchBusEvacuatie.mutate({ dossierId, treinId: trein.id, ...patchPayload }, mutateOptions);
            return;
        }

        const postPayload: BusEvacuatieAanvraagDto = {
            vervoerder: data.vervoerder as BusEvacuatieAanvraagDto["vervoerder"],
            aantalBussen: Number(data.aantalBussen),
            naarStation: data.naarStation,
            opstelLocatie: data.opstelLocatie,
        };

        postBusEvacuatie.mutate({ dossierId, treinId: trein.id, ...postPayload }, mutateOptions);
    }

    const isPending = postBusEvacuatie.isPending || patchBusEvacuatie.isPending;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContainer>
                <DialogHeader className="border-b pb-3">
                    <DialogTitle>
                        {isEdit ? "Aanpassen" : "Bestellen"} evacuatiebus {BetrokkenTrein.formatTrainLabel(trein)}
                    </DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogBody className="grid gap-4">
                            <div className="grid grid-cols-8 gap-1">
                                <div className={isEdit ? "col-span-2" : "col-span-6"}>
                                    <SelectFormField
                                        form={form}
                                        name="vervoerder"
                                        options={vervoerderOptions}
                                        label="Vervoerder"
                                        readOnly={isEdit}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <FormFieldInput form={form} name="aantalBussen" label="Aantal bussen" />
                                </div>
                            </div>
                            {errors.root && <FormErrorMessage>{errors.root.message}</FormErrorMessage>}
                            <TextAreaFormField form={form} name="opstelLocatie" label="Opstellocatie" />
                            <StationsAutocomplete form={form} name="naarStation" label="Afzetstation" />
                            {postBusEvacuatie.error && <ErrorDisplay error={postBusEvacuatie.error} />}
                            {patchBusEvacuatie.error && <ErrorDisplay error={patchBusEvacuatie.error} />}
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" variant="tertiary" onClick={() => handleOpenChange(false)}>
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={isPending}>
                                {isEdit ? "Opslaan" : "Toevoegen"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
